import React, { useState, useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { ArrowLeft, Play } from "lucide-react";
import "./appTourPage.css";
import MobileTopBar from "../../components/MobileTopBar";
import DesktopTopBar from "../../components/DesktopTopBar";
import dogTourIcon from "../../assets/progressIcon6.png";
import videoThumbnail from "../../assets/video_thumbnail.png";

const AppTourPage = () => {
  const navigate = useNavigate();
  const [isPlaying, setIsPlaying] = useState(false);

  // Initialize state from sessionStorage using a lazy initializer
  const [isNearEnd, setIsNearEnd] = useState(() => {
    return sessionStorage.getItem("appTourNearEnd") === "true";
  });

  // Refs to hold the player instance and interval ID
  const playerRef = useRef(null);
  const intervalRef = useRef(null);
  const fullscreenContainerRef = useRef(null);

  useEffect(() => {
    // Load the YouTube IFrame Player API asynchronously on mount
    if (!window.YT) {
      const tag = document.createElement("script");
      tag.src = "https://www.youtube.com/iframe_api";
      const firstScriptTag = document.getElementsByTagName("script")[0];
      firstScriptTag.parentNode.insertBefore(tag, firstScriptTag);
    }

    // Cleanup polling interval if component unmounts
    return () => {
      if (intervalRef.current) clearInterval(intervalRef.current);
    };
  }, []);

  // Function to check the video time
  const checkTime = () => {
    if (playerRef.current && playerRef.current.getCurrentTime) {
      const currentTime = playerRef.current.getCurrentTime();
      const duration = playerRef.current.getDuration();

      // If duration is loaded and we are within the last 5 seconds
      if (duration > 0 && duration - currentTime <= 5) {
        setIsNearEnd(true);
        // Save to sessionStorage when the threshold is reached
        sessionStorage.setItem("appTourNearEnd", "true");

        // Stop checking once we hit the threshold to save resources
        if (intervalRef.current) clearInterval(intervalRef.current);
      }
    }
  };

  // Handle YouTube player state changes
  const onPlayerStateChange = (event) => {
    // event.data === 1 means the video is PLAYING
    if (event.data === 1) {
      // Start checking the time every second
      // Only start polling if they haven't already reached the end
      if (!isNearEnd) {
        intervalRef.current = setInterval(checkTime, 1000);
      }
    } else {
      // Pause checking if the video is paused/buffering/ended
      if (intervalRef.current) clearInterval(intervalRef.current);
    }
  };

  const handlePlayVideo = () => {
    if (isPlaying) return;
    setIsPlaying(true);

    // Request fullscreen on the container element immediately on click
    const container = fullscreenContainerRef.current;
    if (container) {
      if (container.requestFullscreen) {
        container
          .requestFullscreen()
          .catch((err) => console.error("Fullscreen error:", err));
      } else if (container.webkitRequestFullscreen) {
        container.webkitRequestFullscreen(); // Safari
      } else if (container.msRequestFullscreen) {
        container.msRequestFullscreen(); // IE11
      }
    }

    // Give React a split second to render the <div id="yt-player">
    // before the YouTube API tries to mount an iframe inside it
    setTimeout(() => {
      if (window.YT && window.YT.Player) {
        playerRef.current = new window.YT.Player("yt-player", {
          videoId: "oCiNnGTkGFM",
          playerVars: {
            playsinline: 0, // Changed to 0 to trigger native mobile fullscreen
            controls: 1,
            rel: 0,
            autoplay: 1,
          },
          events: {
            onStateChange: onPlayerStateChange,
          },
        });
      }
    }, 100);
  };

  return (
    <div className="sp-tour-page">
      {/* Top Bar */}
      <div className="sp-tour-topbar">
        <MobileTopBar
          showBackButton={false}
          backColor="#000"
          logoColor="#009fd9"
          blueLogo="true"
          isProtectedRoute={false}
        />
        <DesktopTopBar showBack={false} backColor="#000" showProgress={false} />
      </div>

      {/* Main Content */}
      <div className="sp-tour-content-wrapper">
        <div className="sp-tour-content">
          <img
            src={dogTourIcon}
            alt="Happy Dog"
            className="sp-tour-dog-icon"
            onError={(e) => {
              e.target.style.display = "none";
            }}
          />
          <h1 className="sp-tour-title">YOU'RE IN!</h1>
          <p className="sp-tour-subtitle">
            Watch a quick tour to get the most out of Social Paws.
          </p>

          <div className="sp-tour-video-card" ref={fullscreenContainerRef}>
            {!isPlaying ? (
              <>
                <img
                  src={videoThumbnail}
                  alt="App Tour Video"
                  className="sp-tour-video-thumb"
                  onClick={handlePlayVideo}
                />
                <button className="sp-tour-play-btn" onClick={handlePlayVideo}>
                  <Play size={32} fill="#000" color="#000" />
                </button>
              </>
            ) : (
              <div
                id="yt-player"
                style={{
                  width: "100%",
                  height: "100%",
                  position: "absolute",
                  top: 0,
                  left: 0,
                  zIndex: 10,
                  backgroundColor: "#000",
                }}
              ></div>
            )}
          </div>
          {/* <button className="sp-tour-primary-btn" onClick={handlePlayVideo}>
            TAKE A WALK THROUGH SOCIAL PAWS!
          </button> */}

          <button
            className="sp-tour-tertiary-btn"
            onClick={() => navigate("/install-prompt-page")}
          >
            {isNearEnd ? "NEXT" : "SKIP FOR NOW"}
          </button>
        </div>
      </div>
    </div>
  );
};

export default AppTourPage;
